import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import PageHeader from "../../components/shared/PageHeader";
import PageCard from "../../components/shared/PageCard";
import { addButtonSx } from "../../components/shared/buttonStyles";
import { useToast } from "../../components/shared/Toast";
import { handleGetDepartments } from "../../api/mosaic/department";
import { handleDeleteDepartment } from "../../api/mosaic/accounts";
import { SimpleTable } from "../../components/mosaic/Home/SimpleTable";
import SimpleModal from "../../components/mosaic/Home/SimpleModal";
import ConfirmDialog from "../../components/shared/ConfirmDialog";
import { useAppSelector } from "../../store/reduxHooks";
import { DepartmentResponse, EntityType, FormAction } from "../../api/mosaic/interface";

const DepartmentsPage: React.FC = () => {
  const user = useAppSelector((state) => state.userSlice.user);
  const { showSuccess, showError } = useToast();

  const [departments, setDepartments] = useState<DepartmentResponse[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDoneSaving, setIsDoneSaving] = useState(false);

  const [openSimpleModal, setOpenSimpleModal] = useState(false);
  const [modal, setModal] = useState<{ type: EntityType; kind: FormAction } | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<{ name: string; company_id?: number }>({ name: "" });

  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setIsLoadingData(true);
        const response = await handleGetDepartments();
        setDepartments(response);
        setIsLoadingData(false);
      } catch (err) {
        setIsLoadingData(false);
        console.error(err);
      }
    };
    if (user) {
      fetchDepartments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoneSaving, user]);

  const handleCreateClick = () => {
    setSelectedDepartment({ name: "" });
    setModal({ type: EntityType.DEPARTMENT, kind: FormAction.CREATE });
    setOpenSimpleModal(true);
  };

  const handleUpdateClick = (rowData: { id: number; name: string; company_id?: number }) => {
    setSelectedDepartment({
      name: rowData.name,
      company_id: rowData.company_id,
    });
    setModal({ type: EntityType.DEPARTMENT, kind: FormAction.UPDATE });
    setOpenSimpleModal(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setOpenConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      try {
        setIsLoading(true);
        await handleDeleteDepartment(deleteId);
        setIsDoneSaving((prev) => !prev);
        showSuccess("Department deleted successfully");
      } catch (err) {
        showError("Error deleting department");
        console.error(err);
      } finally {
        setIsLoading(false);
        setOpenConfirmation(false);
        setDeleteId(null);
      }
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageHeader title="Departments" subtitle="Manage departments">
        <Button variant="contained" onClick={handleCreateClick} sx={addButtonSx}>
          Add new Department
        </Button>
      </PageHeader>
      <PageCard>
        {isLoadingData ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <SimpleTable
            handleUpdateClick={handleUpdateClick}
            tableData={departments}
            head={["Department Name"]}
            handleDelete={handleDeleteClick}
            searchPlaceholder="Search departments..."
          />
        )}
      </PageCard>

      {modal && (
        <SimpleModal
          open={openSimpleModal}
          setOpen={setOpenSimpleModal}
          setIsDoneSaving={setIsDoneSaving}
          rowData={selectedDepartment}
          kind={modal.kind}
          type={modal.type}
        />
      )}

      <ConfirmDialog
        open={openConfirmation}
        title="Delete Department?"
        message="Are you sure you want to delete this department?"
        onCancel={() => setOpenConfirmation(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
      />
    </Box>
  );
};

export default DepartmentsPage;
